import { type InternalRefetchQueriesInclude } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import { Alert, LinearProgress } from '@mui/material';
import {
  Calculator,
  CircleDollarSign,
  FileText,
  Gavel,
  Home,
  Images,
  LineChart,
  Trash2,
} from 'lucide-react';
import {
  Navigate,
  Route,
  Routes,
  useMatch,
  useParams,
  useSearchParams,
} from 'react-router-dom';

import { Drawer } from '@modules/_shared/components/drawer/Drawer';
import {
  type DrawerTab,
  generateTab,
} from '@modules/_shared/components/drawer/DrawerTopBar';
import { PropertyForm } from '@modules/_shared/components/property-form/PropertyForm';
import { TransactionCommissionsForm } from '@modules/_shared/components/transactions/TransactionCommissionsForm';
import { useNavigateWithSearchParams } from '@modules/_shared/hooks/useNavigateWithSearchParams';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { ListingPropertyImages } from '@modules/listings/ListingPropertyImages/ListingPropertyImages';
import { canReadCommissions } from '@modules/transactions/commissionAccess';
import { TransactionAnalysis } from '@modules/transactions/TransactionAnalysis';
import { TransactionCompliance } from '@modules/transactions/TransactionCompliance';
import { TransactionFiles } from '@modules/transactions/TransactionFiles';
import { TransactionForm } from '@modules/transactions/TransactionForm';
import {
  DELETE_TRANSACTION,
  DRAWER_GET_PROPERTY_TRANSACTION,
} from '@modules/transactions/transactionQueries';
import { useLocale } from '@shared/hooks/locale';
import { isRATenantId } from '@shared/utils/tenant';

interface TransactionDrawerProps {
  maxWidth?: number;
  readOnly?: boolean;
  refetchQueries?: InternalRefetchQueriesInclude;
}

export const TransactionDrawer = ({
  maxWidth = 960,
  readOnly = false,
  refetchQueries,
}: TransactionDrawerProps) => {
  const { t } = useLocale();
  const navigate = useNavigateWithSearchParams();
  const [searchParams] = useSearchParams();
  const { transactionId } = useParams() as { transactionId: string };
  const { me } = useAppData();

  const { data, error, loading } = useQuery(DRAWER_GET_PROPERTY_TRANSACTION, {
    variables: { id: transactionId },
  });

  const [deleteTransaction] = useMutation(DELETE_TRANSACTION);

  const matchA = useMatch('/transactions/:transactionId/:tabName/*');
  const matchB = useMatch(
    '/teams/:teamId/transactions/:transactionId/:tabName/*',
  );
  const matchC = useMatch(
    '/listings/:lotId/transaction/:transactionId/:tabName/*',
  );

  const tabName =
    matchA?.params.tabName ?? matchB?.params.tabName ?? matchC?.params.tabName;

  const tabsDef: [string, string, React.ReactElement?][] = [
    [t('Transaction'), 'transaction', <CircleDollarSign size={20} />],
  ];

  const showPropertyTabs =
    data?.property_transactions_by_pk != null &&
    data.property_transactions_by_pk.lot_id == null;

  const transaction = data?.property_transactions_by_pk;
  const canSeeCommissions =
    me != null && transaction != null && canReadCommissions(me, transaction);

  const showComplianceTab = me?.is_admin && isRATenantId(me?.tenant_id ?? '');

  if (showPropertyTabs) {
    tabsDef.push([t('Property'), 'property', <Home size={20} />]);
    tabsDef.push([t('Images'), 'images', <Images size={20} />]);
    tabsDef.push([t('Files'), 'files', <FileText size={20} />]);
  }

  if (canSeeCommissions) {
    tabsDef.push([t('Commissions'), 'commissions', <Calculator size={20} />]);
  }

  if (showComplianceTab) {
    tabsDef.push([t('Compliance'), 'compliance', <Gavel size={20} />]);
  }

  if (me?.can_edit_settings) {
    tabsDef.push([t('Analysis'), 'analysis', <LineChart size={20} />]);
  }
  const tabs: DrawerTab[] = tabsDef.map(([label, value, icon]) =>
    generateTab([searchParams.toString(), label, value, icon]),
  );

  const handleClose = () => navigate('../');

  const drawerActions = [
    {
      label: t('Delete transaction'),
      disabled: false,
      onClick: () => {
        if (
          window.confirm(t('Are you sure you want to delete this transaction?'))
        ) {
          // Delete team and remove from cache
          deleteTransaction({
            variables: {
              id: transactionId,
            },
            update: cache => {
              cache.evict({ id: `property_transactions:${transactionId}` });
            },
          }).then(() => {
            // Close drawer
            handleClose();
          });
        }
      },
      icon: <Trash2 size={18} />,
    },
  ];

  // Team leaders can update transactions on which either buyer_broker_team_id or seller_broker_team_id is set to a team they lead
  // Team members cannot update transactions
  const isReadOnly =
    readOnly ||
    (!me?.is_admin &&
      !me?.teams
        .filter(t => t.is_leader)
        .some(
          ({ team_id }) =>
            team_id ===
              data?.property_transactions_by_pk?.buyer_broker_team_id ||
            team_id ===
              data?.property_transactions_by_pk?.seller_broker_team_id,
        ));

  return (
    <Drawer
      onClose={handleClose}
      title={t('Edit property transaction')}
      currentTab={tabName ?? 'transaction'}
      tabs={tabs}
      actions={isReadOnly ? undefined : drawerActions}
      maxWidth={maxWidth}
    >
      {error && (
        <Alert severity="error" sx={{ m: 2 }}>
          <pre>{JSON.stringify(error, null, 2)}</pre>
        </Alert>
      )}

      {loading && (
        <LinearProgress
          sx={{
            position: 'absolute',
            top: 0,
            width: '100%',
            zIndex: 2000,
          }}
        />
      )}

      <Routes>
        <Route
          index
          element={
            <Navigate
              replace
              to={{
                pathname: 'transaction',
                search: searchParams.toString(),
              }}
            />
          }
        />

        <Route
          path="transaction"
          element={
            <TransactionForm
              transactionId={transactionId}
              fullAccess={me?.is_admin}
              hideListingField={showPropertyTabs}
              onCancel={handleClose}
              refetchQueries={refetchQueries}
              readonly={isReadOnly}
              preventListingChange
            />
          }
        />
        <Route
          path="commissions"
          element={
            canSeeCommissions ? (
              <TransactionCommissionsForm
                transactionId={transactionId}
                readonly={isReadOnly}
                onCancel={handleClose}
                refetchQueries={refetchQueries}
              />
            ) : (
              // A bookmarked or shared link must not leave an empty drawer.
              transaction != null && (
                <Navigate
                  replace
                  to={{
                    pathname: '../transaction',
                    search: searchParams.toString(),
                  }}
                />
              )
            )
          }
        />
        {showPropertyTabs && (
          <>
            <Route
              path="images/*"
              element={
                <ListingPropertyImages
                  propertyId={data?.property_transactions_by_pk?.property_id}
                  canEditImagesMeta={false}
                />
              }
            >
              <Route path=":imgId" />
            </Route>
            <Route
              path="property"
              element={
                <PropertyForm
                  propertyId={data?.property_transactions_by_pk?.property_id}
                  onCancel={isDirty => !isDirty && handleClose()}
                />
              }
            />
            <Route
              path="files/*"
              element={<TransactionFiles transactionId={transactionId} />}
            />
          </>
        )}
        {me?.can_edit_settings && (
          <Route
            path="analysis"
            element={<TransactionAnalysis transactionId={transactionId} />}
          />
        )}
        {showComplianceTab && (
          <Route
            path="compliance/*"
            element={<TransactionCompliance transactionId={transactionId} />}
          />
        )}
      </Routes>
    </Drawer>
  );
};

export default TransactionDrawer;
